

import { Injectable } from '@angular/core';
import { Observable, of, throwError, from, forkJoin, BehaviorSubject, ReplaySubject } from 'rxjs';
import { map, switchMap, catchError, first, tap, concatMap, finalize, shareReplay } from 'rxjs/operators';
import moment from 'moment';
import _ from 'lodash';

import { ApiService } from '../provider/api.service';
import { endpoints } from 'src/app/constant/endpoints';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class MenuDataService {
  private readonly weekDayArray = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  private _menuCache: { [key: string]: any } = {};
  private _subscriptionsCache: { [key: number]: any } = {};
  private _cacheTimestamps: { [key: string]: number } = {};
  private _rawMenuCache: { [key: string]: any } = {};

  // Store the timer reference to clear it if needed
  private cacheRefreshTimer: any;
  private readonly REFRESH_INTERVAL = 15 * 60 * 1000; // 15 minutes in milliseconds

  // New centralized request management
  private inFlightObservables = new Map<string, Observable<any>>();

  constructor(private http: ApiService, private authService: AuthService) {
    this.loadAllTimestampsFromCache();
    this.startCacheRefreshTimer();
  }
  public fetchAndCacheSubscriptions(): Observable<any | null> {
    const customerId = this.authService.getAuthUser()?.id;
    if (!customerId) {
      console.warn('Customer ID not available. Cannot fetch subscriptions.');
      return of(null);
    }

    const key = this.getSubscriptionsCacheKey(customerId);
    if (this.inFlightObservables.has(key)) {
      return this.inFlightObservables.get(key);
    }
    const cachedSubs = this.getSubscriptionsFromCache(customerId);
    if (cachedSubs) {
      return of(cachedSubs);
    }

    const subObservable = this.fetchMySubscription(customerId).pipe(
      tap((subscriptions) => {
        if (subscriptions) {
          // Changed: getFilteredDates will now include past dates
          const availableDates = this.getFilteredDates(subscriptions);
          const dayConfigMap = this.generateDayConfig(subscriptions, availableDates);
          this.saveSubscriptionsToCache(customerId, subscriptions, availableDates, dayConfigMap);
          const datesToPrecache = availableDates.slice(0, 4);
          this.precacheDates(datesToPrecache, dayConfigMap);
        }
      }),
      catchError((error) => {
        console.error('Failed to get subscriptions:', error);
        return of(null);
      }),
      shareReplay(1),
      finalize(() => this.inFlightObservables.delete(key)),
    );
    this.inFlightObservables.set(key, subObservable);
    return subObservable;
  }
  public getSubscriptionsDataFromCache(): any | null {
    const customerId = this.authService.getAuthUser()?.id;
    if (!customerId) {
      console.warn('Customer ID not available.');
      return null;
    }
    return this.getSubscriptionsFromCache(customerId);
  }
  public fetchMenuToCache(date: string, isDoubleBox: boolean = false): Observable<any> {
    const customerId = this.authService.getAuthUser()?.id;
    if (!customerId) {
      return of(undefined);
    }

    const key = this.getMenuCacheKey(customerId, date, isDoubleBox);

    if (this.inFlightObservables.has(key)) {
      return this.inFlightObservables.get(key);
    }

    // Check for cached transformed data first
    const cachedData = this.getMenuFromCache(date, isDoubleBox);
    if (cachedData) {
      return of(cachedData);
    }

    // Get subscription config to decide if we should transform the data
    const cachedSubs = this.getSubscriptionsFromCache(customerId);
    const configForDate = cachedSubs?.dayConfigMap?.[date];

    // Create the API observable
    const apiObservable = this.fetchMenuBuilderApi(date, isDoubleBox, customerId).pipe(
      tap((rawData) => {
        // Always save raw API response separately
        this.saveRawMenuToCache(customerId, date, rawData, isDoubleBox);
      }),
      map((menuItems) => {
        // Only process and transform if a valid config exists for the date
        if (configForDate) {
          return this.processMenuApiResponse(menuItems, configForDate);
        } else {
          // Return raw data if no config is available, but don't save to _menuCache
          return undefined; // or null, to signify no processed data
        }
      }),
      tap((processedData) => {
        // Only save transformed data if it exists
        if (processedData) {
          this.saveMenuToCache(customerId, date, processedData, isDoubleBox);
        }
      }),
      catchError((error) => {
        console.error('Failed to get menu for date:', error);
        return of(undefined);
      }),
      shareReplay(1),
      finalize(() => {
        this.inFlightObservables.delete(key);
      }),
    );

    this.inFlightObservables.set(key, apiObservable);
    return apiObservable;
  }
  public forceFetchMenuToCache(date: string, isDoubleBox: boolean = false): Observable<any> {
    const customerId = this.authService.getAuthUser()?.id;
    if (!customerId) {
      return of(undefined);
    }

    const key = this.getMenuCacheKey(customerId, date, isDoubleBox);

    if (this.inFlightObservables.has(key)) {
      return this.inFlightObservables.get(key);
    }

    const cachedSubs = this.getSubscriptionsFromCache(customerId);
    const configForDate = cachedSubs?.dayConfigMap?.[date];

    const apiObservable = this.fetchMenuBuilderApi(date, isDoubleBox, customerId).pipe(
      tap((rawData) => {
        this.saveRawMenuToCache(customerId, date, rawData, isDoubleBox);
      }),
      map((menuItems) => {
        if (configForDate) {
          return this.processMenuApiResponse(menuItems, configForDate);
        } else {
          return undefined;
        }
      }),
      tap((processedData) => {
        if (processedData) {
          this.saveMenuToCache(customerId, date, processedData, isDoubleBox);
        }
      }),
      catchError((error) => {
        console.error('Failed to get menu for date:', error);
        return of(undefined);
      }),
      shareReplay(1),
      finalize(() => {
        this.inFlightObservables.delete(key);
      }),
    );

    this.inFlightObservables.set(key, apiObservable);
    return apiObservable;
  }
  public triggerMenuRefresh(date: string, isDoubleBox: boolean = false): void {
    this.forceFetchMenuToCache(date, isDoubleBox).subscribe();
  }
  public getMenuFromCache(date: string, isDoubleBox: boolean = false): any | null {
    const customerId = this.authService.getAuthUser()?.id;
    if (!customerId) {
      console.warn('Customer ID not available.');
      return null;
    }
    const key = this.getMenuCacheKey(customerId, date, isDoubleBox);
    if (this._menuCache[key]) {
      return this._menuCache[key];
    }
    try {
      const storedData = localStorage.getItem(key);
      if (storedData) {
        const data = JSON.parse(storedData);
        this._menuCache[key] = data;
        return data;
      }
    } catch (e) {
      console.error('Error retrieving menu from localStorage:', e);
    }
    return null;
  }
  public updateCachedMenuData(date: string, updatedData: any, isDoubleBox: boolean = false): void {
    const customerId = this.authService.getAuthUser()?.id;
    if (!customerId) {
      console.warn('Customer ID not available. Cannot update cache.');
      return;
    }
    this.saveMenuToCache(customerId, date, updatedData, isDoubleBox);
  }
  public startCacheRefreshTimer(): void {
    if (this.cacheRefreshTimer) {
      clearInterval(this.cacheRefreshTimer);
    }
    this.cacheRefreshTimer = setInterval(() => {
      console.log('Checking for stale menu data to refresh...');
      this.refreshStaleCache();
    }, this.REFRESH_INTERVAL);
  }
  private refreshStaleCache(): void {
    const customerId = this.authService.getAuthUser()?.id;
    if (!customerId) {
      console.warn('Customer ID not available. Cannot refresh cache.');
      return;
    }

    const allCacheKeys = Object.keys(this._cacheTimestamps);
    const now = Date.now();

    allCacheKeys.forEach((key) => {
      const timestamp = this._cacheTimestamps[key];
      if (now - timestamp > this.REFRESH_INTERVAL) {
        const parts = key.split('_');
        const date = parts[3];
        const isDoubleBox = parts[4] === 'box2';
        console.log(`Refreshing stale cache for date: ${date}, doubleBox: ${isDoubleBox}`);
        this.fetchMenuToCache(date, isDoubleBox).subscribe();
      }
    });
  }
  private loadAllTimestampsFromCache(): void {
    try {
      const storedTimestamps = localStorage.getItem('menu_cache_timestamps');
      if (storedTimestamps) {
        this._cacheTimestamps = JSON.parse(storedTimestamps);
      }
    } catch (e) {
      console.error('Error loading timestamps from localStorage:', e);
    }
  }
  private saveAllTimestampsToCache(): void {
    try {
      localStorage.setItem('menu_cache_timestamps', JSON.stringify(this._cacheTimestamps));
    } catch (e) {
      console.error('Error saving timestamps to localStorage:', e);
    }
  }
  private fetchAndCacheMenusForDate(date: string, dayConfigMap: any): Observable<any> {
    const isDoubleBox = dayConfigMap[date]?.isDoubleBox ?? false;
    const box1Observable = this.fetchMenuToCache(date, false);
    if (isDoubleBox) {
      const box2Observable = this.fetchMenuToCache(date, true);
      return forkJoin([box1Observable, box2Observable]);
    } else {
      return box1Observable;
    }
  }
  private precacheDates(dates: string[], dayConfigMap: any): void {
    if (!dates || dates.length === 0) return;

    from(dates)
      .pipe(concatMap((date) => this.fetchAndCacheMenusForDate(date, dayConfigMap)))
      .subscribe({
        next: () => console.log('Pre-caching step complete.'),
        error: (e) => console.error('Error during precaching:', e),
      });
  }
  public triggerSmartPrecaching(currentDate: string): void {
    const customerId = this.authService.getAuthUser()?.id;
    if (!customerId) {
      return;
    }
    const cachedSubs = this.getSubscriptionsDataFromCache();
    if (!cachedSubs) {
      return;
    }

    const { availableDates, dayConfigMap } = cachedSubs;
    const currentIndex = availableDates.indexOf(currentDate);

    if (currentIndex === -1) {
      return;
    }

    const datesToPrecache: string[] = [];

    for (let i = 1; i <= 2; i++) {
      if (currentIndex + i < availableDates.length) {
        datesToPrecache.push(availableDates[currentIndex + i]);
      }
    }

    for (let i = 1; i <= 2; i++) {
      if (currentIndex - i >= 0) {
        datesToPrecache.push(availableDates[currentIndex - i]);
      }
    }

    const uniqueDatesToPrecache = [...new Set(datesToPrecache)];
    this.precacheDates(uniqueDatesToPrecache, dayConfigMap);
  }
  private saveSubscriptionsToCache(customerId: number, subscriptions: any[], availableDates: string[], dayConfigMap: any): void {
    const key = this.getSubscriptionsCacheKey(customerId);
    const data = { subscriptions, availableDates, dayConfigMap };
    this._subscriptionsCache[key] = data;
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch (e) {
      console.error('Error saving subscriptions to localStorage:', e);
    }
  }
  private saveRawMenuToCache(customerId: number, date: string, data: any, isDoubleBox: boolean): void {
    const key = this.getRawMenuCacheKey(customerId, date, isDoubleBox);
    this._rawMenuCache[key] = data;
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch (e) {
      console.error('Error saving raw menu to localStorage:', e);
    }
  }
  public getRawMenuFromCache(date: string, isDoubleBox: boolean = false): Observable<any | null> {
    const customerId = this.authService.getAuthUser()?.id;
    if (!customerId) {
      console.warn('Customer ID not available.');
      return of(null);
    }

    const key = this.getRawMenuCacheKey(customerId, date, isDoubleBox);

    if (this._rawMenuCache[key]) {
      return of(this._rawMenuCache[key]);
    }

    try {
      const storedData = localStorage.getItem(key);
      if (storedData) {
        const data = JSON.parse(storedData);
        this._rawMenuCache[key] = data;
        return of(data);
      }
    } catch (e) {
      console.error('Error retrieving raw menu from localStorage:', e);
    }

    console.log(`Raw menu for ${date} not cached. Fetching...`);
    return this.fetchMenuToCache(date, isDoubleBox).pipe(
      map(() => {
        return this._rawMenuCache[key];
      }),
    );
  }
  private getRawMenuCacheKey(customerId: number, date: string, isDoubleBox: boolean): string {
    return `raw_menu_data_${customerId}_${date}_${isDoubleBox ? 'box2' : 'box1'}`;
  }
  public clearAllCache(): void {
    this._menuCache = {};
    this._subscriptionsCache = {};
    this._cacheTimestamps = {};
    this.inFlightObservables.clear();

    const customerId = this.authService.getAuthUser()?.id;
    const subscriptionsKey = this.getSubscriptionsCacheKey(customerId);
    localStorage.removeItem(subscriptionsKey);
    localStorage.removeItem('menu_cache_timestamps');

    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('menu_data_') || key.startsWith('raw_menu_data_'))) {
        localStorage.removeItem(key);
      }
    }

    if (this.cacheRefreshTimer) {
      clearInterval(this.cacheRefreshTimer);
      this.cacheRefreshTimer = null;
    }
  }
  private getSubscriptionsFromCache(customerId: number): any | null {
    const key = this.getSubscriptionsCacheKey(customerId);
    if (this._subscriptionsCache[key]) {
      return this._subscriptionsCache[key];
    }
    try {
      const storedData = localStorage.getItem(key);
      if (storedData) {
        const data = JSON.parse(storedData);
        this._subscriptionsCache[key] = data;
        return data;
      }
    } catch (e) {
      console.error('Error retrieving subscriptions from localStorage:', e);
    }
    return null;
  }
  private getSubscriptionsCacheKey(customerId: number): string {
    return `subscriptions_${customerId}`;
  }
  private saveMenuToCache(customerId: number, date: string, data: any, isDoubleBox: boolean): void {
    const key = this.getMenuCacheKey(customerId, date, isDoubleBox);
    this._menuCache[key] = data;
    this._cacheTimestamps[key] = Date.now();
    try {
      localStorage.setItem(key, JSON.stringify(data));
      this.saveAllTimestampsToCache();
    } catch (e) {
      console.error('Error saving menu to localStorage:', e);
    }
  }
  private getMenuCacheKey(customerId: number, date: string, isDoubleBox: boolean): string {
    return `menu_data_${customerId}_${date}_${isDoubleBox ? 'box2' : 'box1'}`;
  }
  private fetchMySubscription(customerId: number): Observable<any[]> {
    const apiUrl = `${endpoints.mySubscriptionsnew}?customer_id=${customerId}`;
    return this.http.get(apiUrl).pipe(
      map((response: any) => response?.data?.subscriptions || []),
      first(),
      catchError((error) => {
        console.error('Error fetching subscriptions:', error);
        return throwError(() => error);
      }),
    );
  }
  private fetchMenuBuilderApi(date: string, isDoubleBox: boolean, customerId: number): Observable<any[]> {
    const isDoubleBoxParam = isDoubleBox.toString();
    const apiUrl = `menu_builders?date=${date}&is_double_box=${isDoubleBoxParam}&customer_id=${customerId}`;
    return this.http.get(apiUrl).pipe(
      map((response: any) => {
        if (response?.data?.menu_builders) {
          return response.data.menu_builders;
        }
        throw new Error('Invalid API response format from menu_builders.');
      }),
      first(),
    );
  }
  private processMenuApiResponse(response: any[], config: any) {
    if (!response || !Array.isArray(response) || !config || _.isEmpty(config)) {
      return { newNestedArray: [] };
    }
    const customer = this.authService.getAuthUser();
    const newNestedArray = response.map((builder) => {
      const transformedTypeData = (builder.type_data || []).map((menuItem) => {
        const transformedReplaceableItems = (menuItem.replaceable_menu_items || []).map((replaceable_item) => ({
          ...replaceable_item,
          type_name: builder.type_name,
          parent_id: menuItem.menu_builder_id,
          a_type_name: builder.a_type_name,
          food_type_id: builder.food_type_id,
          food_type_category: menuItem.food_type_category,
          quantity: replaceable_item.quantity || 0,
          is_selected: replaceable_item.is_selected || false,
          selection_number: replaceable_item.selection_number ? replaceable_item.selection_number : '',
          scaled_carbs: replaceable_item.carbs || 0,
        }));

        const isAnyReplacementSelected = transformedReplaceableItems.some((item) => item.is_selected);

        return {
          ...menuItem,
          health_code: this.checkMenuItemPrefrences(menuItem.recipes, customer.allergies, customer.dislikes),
          type_name: builder.type_name,
          a_type_name: builder.a_type_name,
          is_replaceable_selected: isAnyReplacementSelected,
          toggle_replaceable: isAnyReplacementSelected,
          food_type_id: builder.food_type_id,
          quantity: menuItem.quantity || 0,
          is_selected: menuItem.is_selected || false,
          selection_number: menuItem.selection_number ? menuItem.selection_number : '',
          scaled_carbs: menuItem.carbs || 0,
          replaceable_menu_items: transformedReplaceableItems,
        };
      });

      const selectedQuantity = transformedTypeData.reduce((total, item) => total + item.quantity, 0);
      const typeLimit = config.selectionLimit[builder.food_type_id] || 0;

      return {
        ...builder,
        type_data: transformedTypeData,
        selected_quantity: selectedQuantity,
        type_limit: typeLimit,
      };
    });
    return { newNestedArray: newNestedArray };
  }
  checkMenuItemPrefrences(recipes, allergies, dislikes) {
    const allergyIds = new Set(allergies.map((item) => item.id));
    const dislikeIds = new Set(dislikes.map((item) => item.id));
    // console.log(allergyIds, dislikeIds);
    let maxConflictCode = 0;

    for (const recipe of recipes) {
      const isMandatory = recipe.is_mandatory_for_menu_item === '1';
      const isAllergy = allergyIds.has(Number(recipe.id));
      const isDislike = dislikeIds.has(Number(recipe.id));
      let currentCode = 0;

      if (isDislike) {
        currentCode = isMandatory ? 20 : 2;
      } else if (isAllergy) {
        currentCode = isMandatory ? 10 : 1;
      }

      if (currentCode > maxConflictCode) {
        maxConflictCode = currentCode;
      }
    }

    return maxConflictCode;
  }
  private generateDayConfig(subscriptions: any[], dates: string[]): any {
    const dayConfig: any = {};
    dates.forEach((date) => {
      const currentDate = moment(date);
      for (const subscription of subscriptions) {
        const startDate = moment(subscription.start_date, 'DD MMMM, YYYY');
        const endDate = moment(subscription.end_date, 'DD MMMM, YYYY');
        if (currentDate.isBetween(startDate, endDate, undefined, '[]')) {
          const detail = subscription.detail || {};
          dayConfig[date] = {
            date,
            isDoubleBox: subscription.double_box_dates?.includes(date) ?? false,
            is_variable_carbs: detail.is_variable_carbs || false,
            isForceFixedMenu: detail.force_fixed_menu || false,
            selectionLimit: detail.selection_limit || [],
            selectedSubscriptionID: subscription.id,
            mealsCount: Number(detail.total_meals) || 0,
            snacksCount: Number(detail.total_snacks) || 0,
            min_calories: Number(detail.minimum_calories) || 0,
            max_calories: Number(detail.maximum_calories) || 9999999,
            max_scaled_carbs: Number(detail.maximum_carbs) || 0,
            default_scaled_carbs: Number(detail.default_carbs) || 50,
            weekDayOff: Number(detail.day_off),
            weekDay: this.weekDayArray[Number(detail.day_off) || 0],
            subscriptLastDate: moment(subscription.end_date, 'DD MMMM,YYYY').format('YYYY-MM-DD'),
          };
          break;
        }
      }
    });
    return dayConfig;
  }
  private getFilteredDates(subscriptions: any[]): string[] {
    const today = moment().add(1, 'day').startOf('day');
    const allValidDates = subscriptions.flatMap((subscription) => {
      const { start_date, end_date, freeze_dates = [], detail = {} } = subscription;
      const freezeDatesSet = new Set(freeze_dates);
      const dayOffRaw = Array.isArray(detail.day_off) ? detail.day_off : detail.day_off != null ? [detail.day_off] : [];
      const dayOffSet = new Set(dayOffRaw.map(Number));
      const allPossibleDates = this.generateDateRange(start_date, end_date);
      return allPossibleDates.filter((dateStr) => {
        const mDate = moment(dateStr);
        // This is the core change: only future dates are considered available
        return !freezeDatesSet.has(dateStr) && !dayOffSet.has(mDate.day()) && mDate.isAfter(today);
      });
    });
    return [...new Set(allValidDates)].sort((a, b) => moment(a).diff(moment(b)));
  }
  private generateDateRange(startDate: string, endDate: string): string[] {
    const dates: string[] = [];
    let currentDate = moment(startDate, 'DD MMMM, YYYY');
    const endMoment = moment(endDate, 'DD MMMM, YYYY');
    while (currentDate.isSameOrBefore(endMoment)) {
      dates.push(currentDate.format('YYYY-MM-DD'));
      currentDate.add(1, 'day');
    }
    return dates;
  }
}

/* for Menu Page seperate Calls

// When subscriptions are fetched, it will precache some dates automatically:

this.menuDataService.fetchAndCacheSubscriptions().subscribe(subs => {
  if (subs) {
    console.log('Subscriptions fetched, some menus precached');
  }
});

// On date change in swiper / slider:

this.menuDataService.fetchMenuToCache(date).subscribe(menu => {
  console.log('Menu for date', date, menu);
});

// To trigger background fetch without waiting for response:
this.menuDataService.triggerMenuRefresh(date);

// To smartly pre-cache near a given date (background only):
this.menuDataService.triggerSmartPrecaching(date);

// To read already cached data (synchronous):
const cachedMenu = this.menuDataService.getMenuFromCache(date);

const rawMenu = this.menuDataService.getRawMenuFromCache(date);
console.log('Raw menu:', rawMenu);

this.menuDataService.getRawMenuFromCache('2025-09-10').subscribe(raw => {
  console.log('Always raw data here:', raw);
});

*/
